import * as React from "react";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface DateTimePickerProps {
 label?: string;
 value: string; // ISO: yyyy-mm-ddTHH:mm
 onChange: (value: string) => void;
 placeholder?: string;
 error?: string;
 disabled?: boolean;
 required?: boolean;
 min?: string; // ISO datetime
 max?: string; // ISO datetime
 className?: string;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
 label,
 value,
 onChange,
 placeholder = "Selecione data e hora",
 error,
 disabled = false,
 required = false,
 min,
 max,
 className,
}) => {
 // Convert ISO string to Date object, or null if empty
 const validDate = React.useMemo(() => {
 if (!value) return null;
 try {
 // Parse yyyy-mm-ddTHH:mm format
 const [datePart, timePart] = value.split('T');
 const [year, month, day] = datePart.split('-').map(Number);
 const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
 
 if (year && month && day) {
 const date = new Date(year, month - 1, day, hours, minutes);
 // Verify the date is valid
 if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
 return date;
 }
 }
 } catch (e) {
 console.error('Error parsing datetime:', e);
 }
 return null;
 }, [value]);

 // Parse min and max dates
 const minDate = React.useMemo(() => {
 if (!min) return undefined;
 try {
 const [datePart, timePart] = min.split('T');
 const [year, month, day] = datePart.split('-').map(Number);
 const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
 if (year && month && day) {
 return new Date(year, month - 1, day, hours, minutes);
 }
 } catch (e) {
 console.error('Error parsing min datetime:', e);
 }
 return undefined;
 }, [min]);

 const maxDate = React.useMemo(() => {
 if (!max) return undefined;
 try {
 const [datePart, timePart] = max.split('T');
 const [year, month, day] = datePart.split('-').map(Number);
 const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
 if (year && month && day) {
 return new Date(year, month - 1, day, hours, minutes);
 }
 } catch (e) {
 console.error('Error parsing max datetime:', e);
 }
 return undefined;
 }, [max]);

 const [selectedDate, setSelectedDate] = React.useState<Date | null>(validDate);
 const [timeValue, setTimeValue] = React.useState<string>(() => {
 if (validDate) {
 const hours = String(validDate.getHours()).padStart(2, '0');
 const minutes = String(validDate.getMinutes()).padStart(2, '0');
 return `${hours}:${minutes}`;
 }
 return '00:00';
 });

 React.useEffect(() => {
 if (validDate) {
 setSelectedDate(validDate);
 const hours = String(validDate.getHours()).padStart(2, '0');
 const minutes = String(validDate.getMinutes()).padStart(2, '0');
 setTimeValue(`${hours}:${minutes}`);
 } else {
 setSelectedDate(null);
 setTimeValue('00:00');
 }
 }, [value]);

 const handleDateSelect = (date: Date | undefined) => {
 if (date) {
 const newDate = new Date(date);
 // Preserve time if already set
 if (selectedDate) {
 newDate.setHours(selectedDate.getHours());
 newDate.setMinutes(selectedDate.getMinutes());
 }
 setSelectedDate(newDate);
 updateValue(newDate, timeValue);
 } else {
 setSelectedDate(null);
 onChange("");
 }
 };

 const handleTimeChange = (time: string) => {
 setTimeValue(time);
 if (selectedDate) {
 const [hours, minutes] = time.split(':').map(Number);
 const newDate = new Date(selectedDate);
 newDate.setHours(hours || 0);
 newDate.setMinutes(minutes || 0);
 updateValue(newDate, time);
 }
 };

 const updateValue = (date: Date, time: string) => {
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const [hours, minutes] = time.split(':').map(Number);
 const hoursStr = String(hours || 0).padStart(2, '0');
 const minutesStr = String(minutes || 0).padStart(2, '0');
 const isoString = `${year}-${month}-${day}T${hoursStr}:${minutesStr}`;
 onChange(isoString);
 };

 return (
 <div className={cn("space-y-2", className)}>
 {label && (
 <Label htmlFor={label} className={required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
 {label}
 </Label>
 )}
 <div className="flex gap-2">
 <Popover>
 <PopoverTrigger asChild>
 <Button
 variant="outline"
 className={cn(
 "flex-1 justify-start text-left font-normal",
 !selectedDate && "text-muted-foreground",
 error && "border-red-500 focus-visible:ring-red-500"
 )}
 disabled={disabled}
 type="button"
 >
 <CalendarIcon className="mr-2 h-4 w-4" />
 {selectedDate ? (
 format(selectedDate, "dd/MM/yyyy", { locale: ptBR })
 ) : (
 <span>{placeholder}</span>
 )}
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0" align="start">
 <Calendar
 mode="single"
 selected={selectedDate || undefined}
 onSelect={handleDateSelect}
 initialFocus
 locale={ptBR}
 disabled={disabled}
 fromDate={minDate}
 toDate={maxDate}
 />
 </PopoverContent>
 </Popover>
 <div className="flex items-center gap-2">
 <Clock className="h-4 w-4 text-muted-foreground" />
 <Input
 type="time"
 value={timeValue}
 onChange={(e) => handleTimeChange(e.target.value)}
 disabled={disabled || !selectedDate}
 className={cn(
 "w-32",
 error && "border-red-500 focus-visible:ring-red-500"
 )}
 />
 </div>
 </div>
 {error && (
 <p className="text-sm text-red-500">{error}</p>
 )}
 </div>
 );
};

